import React, {ReactElement, useContext} from "react";
import dayjs, {Dayjs} from "dayjs";
import {
    MasterScheduleEvent, MasterScheduleFlags,
    MotorFamily,
    ProductionLine, ProductionLineWithEvents,
    ScheduledTruck,
    ShortTimeFormat
} from "../api/models";
import {SWRResponse} from "swr";
import Utils from "../util/util";
import {TruckReportDto} from "../pages/reports/truckReport";

export interface FamilyLineDetail {
    family: MotorFamily,
    line: ProductionLine,
    lineOpen: number;
    serials: ScheduledTruck[];
    firstTaktEvent: MasterScheduleEvent | undefined;
    taktRate: number;
}

export interface ProductionLineDetail {
    line: ProductionLine,
    bevLineOpen: number;
    iceLineOpen: number;
    iceSerials: ScheduledTruck[];
    bevSerials: ScheduledTruck[];
    firstTaktEvent: MasterScheduleEvent | undefined;
    iceTaktRate: number;
    bevTaktRate: number;
}


export interface MasterScheduleSlotsContextType {
  selectedDate?: Dayjs
  selectedProductionLines?: ProductionLine[]
  lineWithEventsAsync?: SWRResponse<ProductionLineWithEvents[] | undefined> | undefined
  unscheduledTrucks?: TruckReportDto[] | undefined
  filteredTrucks?: TruckReportDto[]
  masterScheduleFlags?: MasterScheduleFlags[]
  getProductionLineDetail?:  (current:Dayjs | undefined, line:ProductionLine | undefined) => ProductionLineDetail | undefined
}

export const getMotorFamilyLineDetail = (fuelType: string | undefined, detail: ProductionLineDetail | undefined) :FamilyLineDetail | undefined => {
    if ( !detail ) return;

    const family = Utils.toMotorFamily( fuelType );
    if ( !family ) return;

    const slotCompare = (a:ScheduledTruck,b:ScheduledTruck) => (a.productionSlot || Number.MAX_VALUE) - (b.productionSlot || Number.MAX_VALUE );

    return family === MotorFamily.BEV
        ? {
            family,
            line: detail.line,
            lineOpen: detail.bevLineOpen,
            serials: detail.bevSerials.sort(slotCompare),
            firstTaktEvent: detail.firstTaktEvent,
            taktRate: detail.bevTaktRate,
        }
        : {
            family,
            line: detail.line,
            lineOpen: detail.iceLineOpen,
            serials: detail.iceSerials.sort(slotCompare),
            firstTaktEvent: detail.firstTaktEvent,
            taktRate: detail.iceTaktRate,
        }
}


const MasterScheduleSlotsContext = React.createContext<MasterScheduleSlotsContextType>({
});

export const useMasterScheduleSlotsContext = () : MasterScheduleSlotsContextType => {
    return useContext(MasterScheduleSlotsContext);
}

// Data provider component
const MasterScheduleSlotsContextProvider = (props:{
  value:Omit<MasterScheduleSlotsContextType, 'getTruckOnDay'>,
  children: ReactElement | ReactElement[]
}) => {

  const isDateInRange = (value: Dayjs | undefined, startDate: Date | undefined, endDate?: Date | undefined): boolean => {

    if (!value) return false;

    const start = dayjs(startDate).utc();
    const end = endDate ? dayjs(endDate).utc() : null;

    return end
        ? (value.isAfter(start, "day") && value.isBefore(end, "day")) ||
        value.isSame(start, "day") ||
        value.isSame(end, "day") // Check if value is within startDate and endDate
        : value.isAfter(start, "day") || value.isSame(start, "day"); // For open-ended ranges, check if value is after or same as startDate
  };



  const getTruckOnDay = (value: Dayjs, productionLine: ProductionLine | undefined): ScheduledTruck[] | undefined => {
    const day = value.format(ShortTimeFormat);
    return props.value.lineWithEventsAsync?.data?.filter(le => le.productionLine === productionLine)
        .reduce((acc: ScheduledTruck[], le) => {
          const trucks = le.scheduledTrucks[day];
          if (!!trucks) {
            return acc.concat(trucks);
          }
          return acc;
        }, []);
  };

  const getListData = (value: Dayjs, productionLine: ProductionLine | undefined): MasterScheduleEvent[] | undefined => {
    return  props.value.lineWithEventsAsync?.data
        ?.find(le => le.productionLine === productionLine)
        ?.events.filter((event) => isDateInRange(value, event.startDate, event.endDate) )
        ?.map((event) => ({
          ...event,
        }));
  };

    const getPreferredEvent = (eventsList: MasterScheduleEvent[] | undefined) : MasterScheduleEvent | undefined => {
        if (!eventsList?.length) {
            return; // Handle empty or undefined list
        }

        return eventsList.reduce((preferredEvent: MasterScheduleEvent, currentEvent: MasterScheduleEvent) => {
            // Case 1: One is non-default, prioritize non-default
            if (!preferredEvent?.isDefault && currentEvent?.isDefault) {
                return preferredEvent;
            }
            if (preferredEvent?.isDefault && !currentEvent?.isDefault) {
                return currentEvent;
            }

            // Case 2: Both are default or both are non-default
            const preferredCreatedAt = preferredEvent?.createdAt ? new Date(preferredEvent.createdAt) : new Date(0); // It's impossible to have null createdAt
            const currentCreatedAt = currentEvent?.createdAt ? new Date(currentEvent.createdAt) : new Date(0);

            // Choose the one with the latest createdAt
            return currentCreatedAt > preferredCreatedAt ? currentEvent : preferredEvent;
        }, eventsList[0]);
    };


    const getProductionLineDetail = (current:Dayjs | undefined, line:ProductionLine | undefined) : ProductionLineDetail | undefined => {
        if ( !current ) return;
        if ( !line ) return;

        const productionLineEventList = getListData?.(current, line);
        if ( !productionLineEventList ) return;

        const firstTaktEvent = getPreferredEvent?.(productionLineEventList);
        const trucksOnLine = getTruckOnDay?.(current, line)

        const bevSerials = trucksOnLine?.filter(t => t?.motorFamily === MotorFamily.BEV) || [];
        const iceSerials = trucksOnLine?.filter(t => t?.motorFamily === MotorFamily.ICE) || [];

        const iceTaktRate= firstTaktEvent?.iceTaktRate || 0;
        const bevTaktRate= firstTaktEvent?.bevTaktRate || 0;

        const iceLineOpen = iceTaktRate - iceSerials.length;
        const bevLineOpen = bevTaktRate - bevSerials.length;

        return {
            line,
            bevLineOpen,
            iceLineOpen,
            iceSerials,
            bevSerials,
            firstTaktEvent,
            iceTaktRate,
            bevTaktRate,
        };


    }


  const quoteContext = props.value;
  return <MasterScheduleSlotsContext.Provider value={{
    ...quoteContext,
      getProductionLineDetail
  }}>{props.children}</MasterScheduleSlotsContext.Provider>;
};
  
export default MasterScheduleSlotsContextProvider;

